import { trimZhLeadingContext } from '@/lib/polymarket/resolvePolymarketDisplayLabel.js';

/**
 * 多语言图例短名：结构化字段之外的兜底压缩。
 * 以「最长公共连续子串」为主（前/中/后均可），辅以前后缀裁切；
 * 按书写系统仅调整分隔符吸附，不做逐语言正则。
 */

const DENSE_SCRIPT_RE =
  /[\u3000-\u303f\u3040-\u30ff\u3400-\u9fff\uf900-\ufaff\uac00-\ud7af\u0e00-\u0e7f\u1000-\u109f]/;

const SEP_CHAR_RE = /[\s\-–—:：|/·•、，,。.]/;
const EDGE_SEP_RE = /^[\s\-–—:：|/·•、，,。.]+|[\s\-–—:：|/·•、，,。.]+$/g;
const TOKEN_SEP_RE = /[\s\-–—:：|/·•、，,。.]+/;

const SHORT_ENOUGH = 28;
const MIN_COMMON_CHARS = 6;

/** @param {string} text */
export function isDenseScriptText(text) {
  const chars = [...String(text || '')].filter((c) => !/\s/.test(c));
  if (!chars.length) return false;
  const dense = chars.filter((c) => DENSE_SCRIPT_RE.test(c)).length;
  return dense / chars.length >= 0.28;
}

/** @param {string[]} names */
function majorityDense(names) {
  return names.filter(isDenseScriptText).length * 2 >= names.length;
}

/** @param {string} s */
function cleanEdges(s) {
  return String(s || '')
    .replace(EDGE_SEP_RE, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** @param {string[]} names */
function commonPrefixLength(names) {
  if (!names.length) return 0;
  let len = 0;
  const first = names[0];
  while (len < first.length && names.every((n) => n[len] === first[len])) len += 1;
  return len;
}

/** @param {string[]} names */
function commonSuffixLength(names) {
  if (!names.length) return 0;
  let len = 0;
  const first = names[0];
  while (
    len < first.length &&
    names.every((n) => n.length > len && n[n.length - 1 - len] === first[first.length - 1 - len])
  ) {
    len += 1;
  }
  return len;
}

/** @param {string} text @param {number} cut @param {boolean} fromStart */
function snapCutToSeparator(text, cut, fromStart) {
  if (cut <= 0 || cut >= text.length) return cut;
  if (fromStart) {
    let i = cut;
    while (i > 0 && !SEP_CHAR_RE.test(text[i - 1])) i -= 1;
    return i >= MIN_COMMON_CHARS ? i : cut;
  }
  let i = text.length - cut;
  while (i < text.length && !SEP_CHAR_RE.test(text[i])) i += 1;
  const snapped = text.length - i;
  return snapped >= MIN_COMMON_CHARS ? snapped : cut;
}

/** @param {string} a @param {string} b */
function longestCommonSubstringTwo(a, b) {
  if (!a || !b) return '';
  const short = a.length <= b.length ? a : b;
  const long = a.length <= b.length ? b : a;
  let best = '';
  for (let start = 0; start < short.length; start += 1) {
    if (short.length - start <= best.length) break;
    for (let end = short.length; end - start > best.length; end -= 1) {
      const sub = short.slice(start, end);
      if (long.includes(sub)) {
        best = sub;
        break;
      }
    }
  }
  return best;
}

/** @param {string[]} strings */
function longestCommonSubstring(strings) {
  if (strings.length < 2) return '';
  let base = strings[0];
  for (let i = 1; i < strings.length; i += 1) {
    base = longestCommonSubstringTwo(base, strings[i]);
    if (base.length < MIN_COMMON_CHARS) return '';
  }
  return base;
}

/**
 * 去掉最长公共连续子串（覆盖重点在前/中/后）。
 * @param {string[]} names
 */
function shortenBySubstringDiff(names) {
  const common = longestCommonSubstring(names);
  if (common.length < MIN_COMMON_CHARS) return names.slice();

  return names.map((n) => {
    const parts = n.split(common).map(cleanEdges).filter(Boolean);
    return parts.join(' ') || n;
  });
}

/**
 * 公共 token 前缀 + 后缀裁切（空格型辅路径）。
 * @param {string[]} names
 */
function shortenByTokenEnds(names) {
  const tokenLists = names.map((n) =>
    n
      .split(TOKEN_SEP_RE)
      .map((t) => t.trim())
      .filter(Boolean)
  );
  if (tokenLists.some((t) => t.length < 2)) return names.slice();

  let pre = 0;
  while (
    tokenLists.every((t) => t.length > pre) &&
    tokenLists.every((t) => t[pre] === tokenLists[0][pre])
  ) {
    pre += 1;
  }

  let suf = 0;
  while (
    tokenLists.every((t) => t.length - pre - suf > 1) &&
    tokenLists.every((t) => t[t.length - 1 - suf] === tokenLists[0][tokenLists[0].length - 1 - suf])
  ) {
    suf += 1;
  }

  if (pre < 1 && suf < 1) return names.slice();

  return tokenLists.map((tokens, i) => {
    const kept = tokens.slice(pre, tokens.length - suf);
    const short = kept.join(' ').trim();
    return short || names[i];
  });
}

/**
 * @param {string[]} names
 * @param {boolean} snapSeparators
 */
function shortenByPrefixSuffix(names, snapSeparators) {
  let pre = commonPrefixLength(names);
  let suf = commonSuffixLength(names);

  if (snapSeparators) {
    pre = snapCutToSeparator(names[0], pre, true);
    suf = snapCutToSeparator(names[0], suf, false);
  }

  const maxCut = Math.min(...names.map((n) => Math.max(0, n.length - 2)));
  if (pre + suf > maxCut) {
    if (pre >= suf) suf = 0;
    else pre = 0;
  }
  if (pre < MIN_COMMON_CHARS) pre = 0;
  if (suf < MIN_COMMON_CHARS) suf = 0;
  if (!pre && !suf) return names.slice();

  return names.map((n) => cleanEdges(n.slice(pre, n.length - suf)) || n);
}

/** @param {string[]} original @param {string[]} shortened */
function compressionHelped(original, shortened) {
  const avg = (arr) => arr.reduce((s, x) => s + x.length, 0) / Math.max(arr.length, 1);
  return avg(shortened) <= avg(original) * 0.82;
}

/** @param {string[]} original @param {string[]} shortened */
function resolveCollisions(original, shortened) {
  const counts = new Map();
  for (const s of shortened) counts.set(s, (counts.get(s) || 0) + 1);
  return shortened.map((s, i) => (counts.get(s) > 1 ? original[i] : s));
}

/**
 * 压缩一组名称（图例用）。已短且互异时可跳过。
 * @param {string[]} names
 * @param {{ force?: boolean }} [opts] force=true：有公共前后缀/子串时仍压缩（行标签兄弟差分）
 * @returns {string[]}
 */
function shortenNameGroup(names, opts = {}) {
  const force = Boolean(opts.force);
  const raw = (Array.isArray(names) ? names : []).map((n) => String(n ?? '').trim());
  // 稠密文案先剥时间状语，避免「在…会议后加息」残留
  const list = raw.map((n) => (isDenseScriptText(n) ? trimZhLeadingContext(n) || n : n));
  if (list.length < 2) return list;

  const unique = new Set(list).size === list.length;
  if (!force && list.every((n) => n.length <= SHORT_ENOUGH) && unique) {
    return list;
  }

  const common = longestCommonSubstring(list);
  const pre = commonPrefixLength(list);
  const suf = commonSuffixLength(list);
  const hasBoilerplate =
    common.length >= MIN_COMMON_CHARS || pre >= MIN_COMMON_CHARS || suf >= MIN_COMMON_CHARS;
  if (force && !hasBoilerplate && unique) {
    return list;
  }

  const dense = majorityDense(list);
  let shortened = shortenBySubstringDiff(list);

  if (!compressionHelped(list, shortened)) {
    const alt = dense ? shortenByPrefixSuffix(list, false) : shortenByTokenEnds(list);
    if (compressionHelped(list, alt) || (force && alt.some((s, i) => s !== list[i]))) {
      shortened = alt;
    } else {
      const pref = shortenByPrefixSuffix(list, !dense);
      if (compressionHelped(list, pref) || (force && pref.some((s, i) => s !== list[i]))) {
        shortened = pref;
      }
    }
  }

  // force 路径：公共前缀/后缀往往比「是否压缩 18%」更重要
  if (force && !shortened.some((s, i) => s !== list[i])) {
    const pref = shortenByPrefixSuffix(list, !dense);
    if (pref.some((s, i) => s !== list[i])) shortened = pref;
  }

  shortened = shortened.map((s, i) => {
    const cleaned = cleanEdges(s) || list[i];
    return isDenseScriptText(cleaned) ? trimZhLeadingContext(cleaned) || cleaned : cleaned;
  });
  return resolveCollisions(list, shortened);
}

/**
 * @param {string[]} names
 * @returns {string[]}
 */
export function shortenChartLegendNames(names) {
  return shortenNameGroup(names, { force: false });
}

/**
 * 兄弟问句差分：剥公共前后缀/子串，得到互异短标（不因「已互异且较短」而跳过）。
 * 用于缺 groupItemTitle 时的多 outcome 行标签兜底。
 * @param {string[]} questions
 * @returns {string[]}
 */
export function differentiateSiblingLabels(questions) {
  const normalized = (Array.isArray(questions) ? questions : []).map((q) =>
    cleanEdges(String(q ?? '').replace(/(?:吗|么)?[？?。.\s]*$/u, ''))
  );
  return shortenNameGroup(normalized, { force: true });
}
